/**
 * Vitrine publique TEXTIPRINT (Sprint 3 MVP).
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001 §2.6.
 *
 * Page accueil = catalogue grille avec filtre famille + recherche.
 * Click produit → /vitrine/produit/:sku
 * Pas de panier — bouton "Commander via WhatsApp" sur fiche produit.
 *
 * Design : layout minimaliste sans sidebar admin, palette `.t-textprint`,
 * white-label (pas de mention SIM GROUP visible).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles, Truck, Phone, ShoppingBag } from 'lucide-react';
import { vitrineApi, type VitrineFamille, type VitrineProduit } from '@/api/textprint-public';

const formatFCFA = (cents: string | number): string => {
  const value = typeof cents === 'string' ? Number(BigInt(cents)) : Number(cents);
  return new Intl.NumberFormat('fr-FR').format(Math.round(value / 100));
};

export const VitrineHomePage = () => {
  const navigate = useNavigate();
  const [familles, setFamilles] = useState<VitrineFamille[]>([]);
  const [produits, setProduits] = useState<VitrineProduit[]>([]);
  const [loading, setLoading] = useState(true);
  const [familleSel, setFamilleSel] = useState<string>('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const fams = await vitrineApi.familles();
        setFamilles(fams ?? []);
      } catch (e) {
        console.error('Failed to load familles', e);
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await vitrineApi.produits({
          limit:   100,
          famille: familleSel || undefined,
          search:  search.trim() || undefined,
        });
        setProduits(res?.data ?? []);
      } catch (e) {
        console.error('Failed to load produits', e);
        setProduits([]);
      } finally {
        setLoading(false);
      }
    }, 200); // debounce search
    return () => clearTimeout(t);
  }, [familleSel, search]);

  const featured = useMemo(() => produits.filter((p) => p.featured).slice(0, 4), [produits]);

  return (
    <div className="t-textprint" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, var(--p-deep), var(--p-dark))',
        color: 'white', padding: '32px 24px', position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h1 style={{ fontFamily: 'var(--font-d)', fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
            <ShoppingBag size={26} style={{ display: 'inline', marginRight: 10, verticalAlign: '-4px' }} />
            BOUTIQUE TEXTIPRINT
          </h1>
          <p style={{ fontSize: 14, opacity: 0.85, margin: '6px 0 0 0' }}>
            L'imprimeur qui vend aussi ce qu'il utilise · Livraison Abidjan 24h
          </p>
        </div>
      </header>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
        {/* Bandeau valeur ajoutée */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { ico: <Truck size={18} />,    txt: 'Livraison Abidjan 24h',                sub: 'Cocody, Plateau, Yopougon...' },
            { ico: <Sparkles size={18} />, txt: 'Personnalisation à la demande',        sub: 'Logo + texte + couleur' },
            { ico: <Phone size={18} />,    txt: 'Commande par WhatsApp',                sub: 'Réponse en moins de 2h' },
          ].map((b, i) => (
            <div key={i} style={{ background: 'white', padding: 14, borderRadius: 10, border: '1px solid var(--bdr)', display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ width: 38, height: 38, borderRadius: 8, background: 'var(--p-soft)', color: 'var(--p)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {b.ico}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--tx)' }}>{b.txt}</div>
                <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{b.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Featured strip */}
        {featured.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontFamily: 'var(--font-d)', fontSize: 18, color: 'var(--tx)', marginBottom: 12 }}>
              ★ Sélection du moment
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {featured.map((p) => <ProduitCard key={p.id} produit={p} onClick={() => navigate(`/vitrine/produit/${encodeURIComponent(p.sku)}`)} />)}
            </div>
          </div>
        )}

        {/* Filtres */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 320 }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx-3)' }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher SKU, nom, marque…"
              style={{
                width: '100%', padding: '10px 12px 10px 34px', borderRadius: 8,
                border: '1px solid var(--bdr)', fontSize: 13, fontFamily: 'inherit',
                background: 'white', color: 'var(--tx)',
              }}
            />
          </div>
          <button
            onClick={() => setFamilleSel('')}
            style={{
              padding: '8px 14px', borderRadius: 8, border: '1px solid var(--bdr)', fontSize: 13,
              background: familleSel === '' ? 'var(--p)' : 'white',
              color:      familleSel === '' ? 'white'   : 'var(--tx)',
              fontWeight: familleSel === '' ? 600 : 400, cursor: 'pointer',
            }}
          >Toutes ({produits.length})</button>
          {familles.map((f) => (
            <button
              key={f.id}
              onClick={() => setFamilleSel(f.code)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: '1px solid var(--bdr)', fontSize: 13,
                background: familleSel === f.code ? 'var(--p)' : 'white',
                color:      familleSel === f.code ? 'white'   : 'var(--tx)',
                fontWeight: familleSel === f.code ? 600 : 400, cursor: 'pointer',
              }}
            >{f.nom}</button>
          ))}
        </div>

        {/* Grille produits */}
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
        ) : produits.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--tx-3)' }}>
            Aucun produit trouvé. <button onClick={() => { setSearch(''); setFamilleSel(''); }} style={{ color: 'var(--p)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Réinitialiser les filtres</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {produits.map((p) => <ProduitCard key={p.id} produit={p} onClick={() => navigate(`/vitrine/produit/${encodeURIComponent(p.sku)}`)} />)}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--bdr)', padding: '24px', marginTop: 40, background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gap: 8, fontSize: 12, color: 'var(--tx-3)', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-d)', fontWeight: 700, fontSize: 15, color: 'var(--tx)' }}>TEXTI PRINT SARL</div>
          <div>Angré, Abidjan · Côte d'Ivoire · RCCM CI-ABJ-03-2021-B12-02551</div>
          <div>Imprimerie · Textile · Broderie · Gadgets publicitaires</div>
        </div>
      </footer>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Carte produit (compact)
// ──────────────────────────────────────────────────────────────────────────
const ProduitCard = ({ produit, onClick }: { produit: VitrineProduit; onClick: () => void }) => (
  <div
    onClick={onClick}
    style={{
      background: 'white', borderRadius: 10, border: '1px solid var(--bdr)',
      overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s',
      display: 'flex', flexDirection: 'column',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--sh-md)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
  >
    <div style={{
      aspectRatio: '1', background: produit.photos[0]
        ? `url(${produit.photos[0]}) center/cover no-repeat`
        : 'linear-gradient(135deg, var(--p-soft), var(--bg))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--p)', fontFamily: 'var(--font-d)', fontWeight: 700, fontSize: 14,
      position: 'relative',
    }}>
      {!produit.photos[0] && (
        <div style={{ textAlign: 'center', padding: 12 }}>
          <ShoppingBag size={28} style={{ marginBottom: 4 }} />
          <div style={{ fontSize: 11, opacity: 0.7 }}>{produit.famille?.nom ?? '—'}</div>
        </div>
      )}
      {produit.personnalisable && (
        <span style={{
          position: 'absolute', top: 8, right: 8,
          background: 'var(--p)', color: 'white',
          padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
        }}>
          <Sparkles size={10} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 2 }} />
          PERSO
        </span>
      )}
      {produit.featured && (
        <span style={{
          position: 'absolute', top: 8, left: 8,
          background: 'gold', color: 'var(--tx)',
          padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
        }}>★</span>
      )}
    </div>
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)', lineHeight: 1.3, minHeight: 34 }}>
        {produit.nom}
      </div>
      <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
        {produit.marque ?? produit.famille?.nom ?? ''}
        {produit.quantiteMinimum && ` · dès ${produit.quantiteMinimum} u.`}
      </div>
      <div style={{ marginTop: 'auto', paddingTop: 8 }}>
        <div style={{ fontFamily: 'var(--font-d)', fontWeight: 700, fontSize: 18, color: 'var(--p)' }}>
          {formatFCFA(produit.prixUnitaireHtCents)} <span style={{ fontSize: 11, fontWeight: 400 }}>FCFA HT</span>
        </div>
      </div>
    </div>
  </div>
);
